import { LayerScopeConfig, ScopeType } from "src/js/stores/site/store";

import { Checkbox } from "react-migration/components/Checkbox";
import { MeasurementSystem } from "src/js/stores/user/store";
import { PlanningApplication } from "react-migration/domains/planning/types";
import { PlanningApplicationDetailMiniCard } from "react-migration/domains/planning/components/PlanningApplicationDetailMiniCard";
import { Table } from "react-migration/components/TableDeprecated";
import { clsx } from "clsx";
import { convertLengthToAllUnits } from "react-migration/lib/util/measurements";
import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import orderBy from "lodash/orderBy";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

type PlanningApplicationPrintableTableProps = {
  planningApplications: PlanningApplication[];
  hiddenIds: string[];
  allVisible: boolean;
  toggleId: (id: string) => void;
  toggleAll: () => void;
  scopeConfig: LayerScopeConfig;
  scopeType?: ScopeType;
  planningIdentifier: (application: PlanningApplication) => string;
};

export const PlanningApplicationPrintableTable = ({
  planningApplications,
  hiddenIds,
  allVisible,
  toggleId,
  toggleAll,
  scopeConfig,
  scopeType = ScopeType.ON_SITE,
  planningIdentifier,
}: PlanningApplicationPrintableTableProps) => {
  const { t } = useTranslation();
  const isImperial = getMeasurementSystemPreference() === MeasurementSystem.IMPERIAL;
  const renderCell = (application: PlanningApplication) => (
    <PlanningApplicationDetailMiniCard application={application} printable />
  );

  const measurementUnit = isImperial ? "feet" : "meters";
  return (
    <>
      <h3 className="text-sm">
        {scopeType === ScopeType.NEARBY && t("site_report.contextual_data.nearby")}{" "}
        <span className="font-normal">
          {scopeType === ScopeType.NEARBY &&
            t("site_report.contextual_data.nearby_within", {
              radius: Math.round(convertLengthToAllUnits(scopeConfig.radius ?? 100, "meters")[measurementUnit]),
              unit: measurementUnit,
            })}
        </span>
      </h3>

      <Table
        size="small"
        primaryKey="ref"
        rowClassName={(planningApplication) =>
          clsx("border-none", {
            "bg-red-50! print:hidden":
              !!planningApplication && hiddenIds.includes(planningIdentifier(planningApplication)),
          })
        }
        headerCellClassName={(column) =>
          clsx({
            "print:hidden": column.id === "checkbox",
          })
        }
        cellClassName={(_, column) =>
          clsx({
            "print:hidden": column.id === "checkbox",
          })
        }
        columns={[
          {
            id: "checkbox",
            label: (
              <div>
                <Checkbox checked={allVisible} onCheckedChange={toggleAll} />
              </div>
            ),
            render: (planningApplication) => (
              <div>
                <Checkbox
                  checked={!hiddenIds.includes(planningIdentifier(planningApplication))}
                  onCheckedChange={() => toggleId(planningIdentifier(planningApplication))}
                />
              </div>
            ),
          },
          {
            label: t("sites.card.site_report.planning.planning_application"),
            render: (application) => renderCell(application),
            grow: true,
          },
        ]}
        rows={orderBy(
          planningApplications.filter(({ ref }) => !hiddenIds.includes(ref)),
          (application) => application.decision_date,
          "desc"
        )}
        tableClassName="block overflow-x-auto max-w-full print:table print:table-fixed print:w-full"
        tableContainerClassName="print:border-none"
      />
    </>
  );
};
